import { useEffect, useState } from 'react';
import {
  formatCurrency,
  isPositiveAmount,
  localDayKey,
  type ExpenseItemWithRelations,
} from '@peles/shared';
import { EntityModal } from '../../common/EntityModal';
import { SearchableListSelect } from '../../common/SearchableListSelect';
import { useDialog } from '../../../context/DialogContext';
import {
  useAddExpensePayment,
  useExpenseConfig,
  useMarkPaidInFull,
} from '../../../hooks/useExpenses';
import { extractApiErrorMessage } from '../../../utils/apiError';
import { ScheduleList } from './ScheduleList';

export interface RecordPaymentSheetProps {
  /** The line being paid; null keeps the sheet closed. */
  item: ExpenseItemWithRelations | null;
  onClose: () => void;
  /** What the amount starts at: the next installment (default) or everything left. */
  start?: 'next' | 'remaining';
}

/** The amount a sheet opens with: the next installment, else what is left. */
export function startingAmount(
  item: ExpenseItemWithRelations,
  start: 'next' | 'remaining' = 'next',
): number {
  if (start === 'next' && item.money.nextDue) return item.money.nextDue.amount;
  return Math.max(0, item.balance);
}

const inputClass =
  'w-full min-h-11 sm:min-h-0 px-3 py-2 border border-gray-300 rounded-md text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

/**
 * Recording a payment against a booked line, reached from its row — prefilled
 * with the next installment (or what is left), never a form below one you have
 * to scroll past. "Pay next" and "Pay remaining" set the amount; "Mark paid in
 * full" has the server record what is left, worked out as it saves.
 */
export function RecordPaymentSheet({ item, onClose, start = 'next' }: RecordPaymentSheetProps) {
  const { data: config } = useExpenseConfig();
  const addPayment = useAddExpensePayment();
  const markPaid = useMarkPaidInFull();
  const { alert } = useDialog();
  const [amount, setAmount] = useState('');
  const [methodId, setMethodId] = useState('');
  const [paidAt, setPaidAt] = useState(localDayKey());
  const [notes, setNotes] = useState('');

  const itemId = item?.id ?? null;
  // A fresh form for each line opened — not for each refetch of the same one.
  useEffect(() => {
    if (!item) return;
    setAmount(String(startingAmount(item, start)));
    setPaidAt(localDayKey());
    setNotes('');
  }, [itemId, start]);

  if (!item) return null;
  const busy = addPayment.isPending || markPaid.isPending;
  const value = Number(amount);
  const amountOk = amount.trim() !== '' && Number.isFinite(value) && value > 0;
  const owed = isPositiveAmount(item.balance);
  const next = item.money.nextDue;
  const name = item.itemName || item.vendorCategoryName;

  const failed = (title: string) => (err: unknown) =>
    void alert({
      title,
      message: extractApiErrorMessage(err, 'Please try again.'),
      variant: 'error',
    });

  const record = () => {
    if (!amountOk || !methodId) return;
    addPayment.mutate(
      {
        expenseItemId: item.id,
        amount: value,
        paymentMethodId: methodId,
        paidAt: paidAt || undefined,
        notes: notes.trim() || undefined,
      },
      { onSuccess: onClose, onError: failed('Payment not recorded') },
    );
  };

  const payInFull = () => {
    if (!methodId) return;
    markPaid.mutate(
      {
        expenseItemId: item.id,
        paymentMethodId: methodId,
        paidAt: paidAt || undefined,
        notes: notes.trim() || undefined,
      },
      { onSuccess: onClose, onError: failed('Not marked paid') },
    );
  };

  const chip =
    'min-h-11 sm:min-h-8 px-3 text-sm rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50';

  return (
    <EntityModal
      open
      onClose={onClose}
      title={`Record payment — ${name}`}
      size="md"
      footer={
        <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2 w-full">
          <button
            type="button"
            onClick={payInFull}
            disabled={!owed || !methodId || busy}
            className="min-h-11 sm:min-h-9 px-3 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Mark paid in full
          </button>
          <button
            type="button"
            onClick={record}
            disabled={!amountOk || !methodId || busy}
            className="min-h-11 sm:min-h-9 px-4 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {addPayment.isPending ? 'Recording…' : 'Record payment'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
          <div className="rounded-md bg-gray-50 px-3 py-2">
            <dt className="text-xs text-gray-500">Price</dt>
            <dd className="font-medium text-gray-900">{formatCurrency(item.price)}</dd>
          </div>
          <div className="rounded-md bg-gray-50 px-3 py-2">
            <dt className="text-xs text-gray-500">Paid</dt>
            <dd className="font-medium text-gray-900">{formatCurrency(item.totalPaid)}</dd>
          </div>
          <div className="rounded-md bg-gray-50 px-3 py-2">
            <dt className="text-xs text-gray-500">Left to pay</dt>
            <dd className={`font-medium ${owed ? 'text-red-600' : 'text-green-600'}`}>
              {formatCurrency(Math.max(0, item.balance))}
            </dd>
          </div>
        </dl>

        {item.installments.length > 0 && <ScheduleList money={item.money} compact />}

        {owed && (
          <div className="flex flex-wrap gap-2" role="group" aria-label="Quick amounts">
            {next && next.amount !== item.balance && (
              <button type="button" className={chip} onClick={() => setAmount(String(next.amount))}>
                Pay next {formatCurrency(next.amount)}
              </button>
            )}
            <button
              type="button"
              className={chip}
              onClick={() => setAmount(String(Math.max(0, item.balance)))}
            >
              Pay remaining {formatCurrency(item.balance)}
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Amount</span>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className={inputClass}
            />
          </label>
          <div>
            <label
              htmlFor="record-payment-method"
              className="block text-xs font-medium text-gray-700 mb-1"
            >
              Method
            </label>
            <SearchableListSelect
              items={config?.paymentMethods ?? []}
              value={methodId || null}
              onChange={(v) => setMethodId(typeof v === 'string' ? v : '')}
              getValue={(m) => m.id}
              getLabel={(m) => m.name}
              placeholder="Select method..."
              ariaLabel="Method"
              inputId="record-payment-method"
            />
          </div>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Date</span>
            <input
              type="date"
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
              className={inputClass}
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Notes</span>
            <input
              type="text"
              value={notes}
              maxLength={2000}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional — a check number, a reference"
              className={inputClass}
            />
          </label>
        </div>
      </div>
    </EntityModal>
  );
}
